import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePermissions, useHasAnyPermission } from '../../context/PermissionsContext';

interface RequirePermissionProps {
  /** Permission strings to check. User needs at least ONE of these (OR logic). */
  permissions?: string[];
  /** If true, requires the user to be a chapter admin. */
  requireAdmin?: boolean;
  /** Child content to render if authorized. */
  children: React.ReactNode;
}

const AccessDenied = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        gap: 2
      }}>
      <LockOutlined sx={{ fontSize: 64, color: 'text.disabled' }} />
      <Typography variant="h5">Access Denied</Typography>
      <Typography variant="body1" sx={{
        color: "text.secondary"
      }}>
        You don't have permission to access this page.
      </Typography>
      <Button variant="outlined" onClick={() => navigate('/')}>
        Go to Dashboard
      </Button>
    </Box>
  );
};

const RequirePermission: React.FC<RequirePermissionProps> = ({
  permissions = [],
  requireAdmin = false,
  children
}) => {
  const { isAdmin, loading } = usePermissions();
  const hasAnyPermission = useHasAnyPermission(permissions);

  if (loading) {
    return null; // Parent Suspense/loading handles this
  }

  // Admin check
  if (requireAdmin && !isAdmin) {
    return <AccessDenied />;
  }

  // Permission check (OR logic: user needs at least one)
  if (permissions.length > 0 && !isAdmin && !hasAnyPermission) {
    return <AccessDenied />;
  }

  return <>{children}</>;
};

export default RequirePermission;
